import { useState, useCallback, useRef, useEffect } from 'react';
import ExcelJS from 'exceljs';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Progress } from '@/components/ui/progress';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';
import { Upload, FileSpreadsheet, CheckCircle2, XCircle, ImageIcon, Pencil, Trash2, Check, X, Trophy, Download } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { CSVUploader } from '@/components/CSVUploader';
import capFormatRef from '@/assets/cap-format-reference.png';
import { logAdminAction, logUpdate, logDelete } from '@/lib/audit-log';
import { DataPagination } from '@/components/DataPagination';

interface CapRow {
  report_date: string | null;
  creator_username: string;
  affiliate_gmv: number;
  affiliate_orders: number;
  affiliate_live_gmv: number;
}

interface UploadRecord {
  uploaded_at: string;
  row_count: number;
}

function parseDollar(val: any): number {
  if (val == null) return 0;
  const s = String(val).replace(/[$,]/g, '').trim();
  const n = parseFloat(s);
  return isNaN(n) ? 0 : n;
}

function parseNum(val: any): number {
  if (val == null) return 0;
  const s = String(val).replace(/,/g, '').trim();
  const n = parseFloat(s);
  return isNaN(n) ? 0 : n;
}

export default function CAPData() {
  const queryClient = useQueryClient();
  const [parsedRows, setParsedRows] = useState<CapRow[]>([]);
  const [parsing, setParsing] = useState(false);
  const [selectedFileName, setSelectedFileName] = useState<string | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadResult, setUploadResult] = useState<{ count: number; timestamp: string } | null>(null);
  const [uploadHistory, setUploadHistory] = useState<UploadRecord[]>([]);
  const [historyTotal, setHistoryTotal] = useState(0);
  const [historyPage, setHistoryPage] = useState(1);
  const [historyDateFrom, setHistoryDateFrom] = useState('');
  const [historyDateTo, setHistoryDateTo] = useState('');
  const [historyPageSize, setHistoryPageSize] = useState(10);
  const [editingRow, setEditingRow] = useState<any | null>(null);
  const [exporting, setExporting] = useState(false);
  const [exportProgress, setExportProgress] = useState(0);
  const fileRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    loadUploadHistory(1, historyDateFrom, historyDateTo);
  }, []);

  const loadUploadHistory = async (page = historyPage, dateFrom = historyDateFrom, dateTo = historyDateTo, pageSize = historyPageSize) => {
    console.log("🚀 ~ loadUploadHistory ~ pageSize:", pageSize)
    const from = (page - 1) * pageSize;
    const to = from + pageSize - 1;

    let query = supabase
      .from('cap_upload_history')
      .select('uploaded_at, row_count', { count: 'exact' })
      .order('uploaded_at', { ascending: false })
      .range(from, to);

    if (dateFrom) query = query.gte('uploaded_at', new Date(dateFrom).toISOString());
    if (dateTo) {
      const end = new Date(dateTo);
      end.setDate(end.getDate() + 1);
      query = query.lt('uploaded_at', end.toISOString());
    }

    const { data, count } = await query;
    console.log("🚀 ~ loadUploadHistory ~ data:", data)
    if (data) setUploadHistory(data);
    if (count != null) setHistoryTotal(count);
  };

  const handleExport = async () => {
    setExporting(true);
    setExportProgress(0);
    try {
      // Step 1: get total count
      const { count } = await supabase
        .from('cap_upload_history')
        .select('*', { count: 'exact', head: true });

      const total = count ?? 0;
      if (total === 0) {
        toast.error('No upload history to export');
        return;
      }

      // Step 2: fetch in batches of 1000
      const batchSize = 1000;
      const allRows: any[] = [];
      let fetched = 0;

      while (fetched < total) {
        const { data, error } = await supabase
          .from('cap_upload_history')
          .select('uploaded_at, row_count')
          .order('uploaded_at', { ascending: false })
          .range(fetched, fetched + batchSize - 1);

        if (error) throw error;
        allRows.push(...(data ?? []));
        fetched += batchSize;
        setExportProgress(Math.min(Math.round((fetched / total) * 90), 90));
      }

      // Step 3: build xlsx
      const wb = new ExcelJS.Workbook();
      const ws = wb.addWorksheet('CAP Upload History');
      ws.columns = [
        { header: 'Upload Date', key: 'uploaded_at', width: 25 },
        { header: 'Row Count',   key: 'row_count',   width: 14 },
      ];
      ws.getRow(1).font = { bold: true };
      ws.addRows(allRows);

      setExportProgress(95);
      const buffer = await wb.xlsx.writeBuffer();
      const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `cap-upload-history-${new Date().toISOString().slice(0, 10)}.xlsx`;
      a.click();
      URL.revokeObjectURL(url);
      setExportProgress(100);
      toast.success(`Exported ${allRows.length.toLocaleString()} records`);
    } catch (err: any) {
      toast.error(err.message ?? 'Export failed');
    } finally {
      setExporting(false);
      setExportProgress(0);
    }
  };

  const processFile = async (file: File) => {
    setUploadResult(null);
    setParsing(true);
    setSelectedFileName(file.name);
    const buffer = await file.arrayBuffer();
    const wb = new ExcelJS.Workbook();
    await wb.xlsx.load(buffer);

    let ws: ExcelJS.Worksheet | undefined;
    wb.eachSheet((sheet) => {
      if (!ws || sheet.name.toLowerCase().includes('custom report')) ws = sheet;
    });
    if (!ws) { toast.error('No worksheet found'); return; }

    const headerRow = ws.getRow(1);
    const headers: Record<number, string> = {};
    headerRow.eachCell((cell, colNumber) => {
      headers[colNumber] = String(cell.value ?? '').trim();
    });

    const normalize = (s: string) => s.toLowerCase().replace(/[^a-z0-9]/g, '');
    const colFor = (key: string) => {
      const nk = normalize(key);
      return Object.entries(headers).find(([, v]) => normalize(v) === nk)?.[0];
    };
    const getVal = (row: ExcelJS.Row, key: string) => {
      const col = colFor(key);
      return col ? (row.getCell(Number(col)).value ?? '') : '';
    };

    const rows: CapRow[] = [];
    ws.eachRow((row, rowNumber) => {
      if (rowNumber === 1) return;

      const creatorUsername = String(
        getVal(row, 'creatorusername') || getVal(row, 'Creator username') || ''
      ).trim();

      if (!creatorUsername || creatorUsername.toLowerCase() === 'summary' || creatorUsername === '-') return;

      rows.push({
        report_date: String(getVal(row, 'date') || '').trim() || null,
        creator_username: creatorUsername,
        affiliate_gmv: parseDollar(getVal(row, 'affiliategmv') || getVal(row, 'Affiliate GMV') || getVal(row, 'gmv')),
        affiliate_orders: parseNum(getVal(row, 'affiliateorders') || getVal(row, 'Affiliate orders')),
        affiliate_live_gmv: parseDollar(getVal(row, 'affiliatelivegmv') || getVal(row, 'Affiliate LIVE GMV')),
      });
    });
    setParsedRows(rows);
    setParsing(false);
  };

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    if (parsing) return;
    const file = e.dataTransfer.files[0];
    if (file && file.name.endsWith('.xlsx')) void processFile(file);
    else toast.error('Please upload a .xlsx file');
  }, [parsing]);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) void processFile(file);
  };

  const handleUpload = async () => {
    if (!parsedRows.length) return;
    setUploading(true);
    setUploadProgress(0);

    const uploadedAt = new Date().toISOString();
    const batchSize = 500;
    let uploaded = 0;

    try {
      // Create the upload history record first so we get back an upload_id to
      // attach to every cap_data row (same pattern as tap_data/tap_upload_history
      // in TAPData.tsx). This is what lets match_cap_acceptances() scope its
      // lookup to exactly this batch instead of a time-window heuristic.
      const { data: historyRow, error: historyInsertError } = await supabase
        .from('cap_upload_history')
        .insert({ uploaded_at: uploadedAt, row_count: parsedRows.length })
        .select('id')
        .single();
      if (historyInsertError) throw historyInsertError;
      const uploadId = historyRow.id as string;

      for (let i = 0; i < parsedRows.length; i += batchSize) {
        const batch = parsedRows.slice(i, i + batchSize).map(row => ({
          ...row,
          uploaded_at: uploadedAt,
          upload_id: uploadId,
        }));
        const { error } = await supabase.from('cap_data').insert(batch);
        if (error) throw error;
        uploaded += batch.length;
        setUploadProgress(Math.round((uploaded / parsedRows.length) * 100));
      }

      const rowCount = parsedRows.length;
      setParsedRows([]);
      if (fileRef.current) fileRef.current.value = '';
      setHistoryPage(1);
      loadUploadHistory(1, historyDateFrom, historyDateTo, historyPageSize);
      setUploadResult({ count: rowCount, timestamp: uploadedAt });

      // Audit log for CAP data upload
      await logAdminAction({
        action: 'CREATE',
        tableName: 'cap_data',
        newValues: { count: rowCount, uploaded_at: uploadedAt },
        metadata: {
          description: `Uploaded ${rowCount} CAP data rows`,
          row_count: rowCount,
          uploaded_at: uploadedAt,
          operation: 'excel_upload'
        }
      });

      // The sheet is the source of truth: everyone in this upload is activated
      // (pending/invite_sent/expired, or reactivated from an auto dropout),
      // everyone active but absent is deactivated. Order matters: match runs
      // first so an in-sheet creator isn't stamped expired by this same upload,
      // and dropouts run last. Each step is logged but never fails the upload.
      let matchedCount = 0;
      try {
        const { data: matched, error: matchError } = await supabase.rpc('match_cap_acceptances', {
          p_upload_id: uploadId,
        });
        if (matchError) throw matchError;
        matchedCount = matched ?? 0;
        await logAdminAction({
          action: 'UPDATE',
          tableName: 'cap_applications',
          metadata: {
            description: `Activated ${matchedCount} CAP application(s) via upload match`,
            matched_count: matchedCount,
            upload_id: uploadId,
            trigger: 'cap_data_upload',
          },
        });
      } catch (matchErr: any) {
        await logAdminAction({
          action: 'UPDATE',
          tableName: 'cap_applications',
          metadata: {
            description: 'CAP acceptance matcher failed after upload',
            status: 'failed',
            error: matchErr.message,
            upload_id: uploadId,
            trigger: 'cap_data_upload',
          },
        });
      }

      let expiredCount = 0;
      try {
        const { data: expired, error: expireError } = await supabase.rpc('expire_cap_invites');
        if (expireError) throw expireError;
        expiredCount = expired ?? 0;
        if (expiredCount > 0) {
          await logAdminAction({
            action: 'UPDATE',
            tableName: 'cap_applications',
            metadata: {
              description: `Flagged ${expiredCount} CAP invite(s) as expired (7+ days, no acceptance)`,
              expired_count: expiredCount,
              upload_id: uploadId,
              trigger: 'cap_data_upload',
            },
          });
        }
      } catch (expireErr: any) {
        await logAdminAction({
          action: 'UPDATE',
          tableName: 'cap_applications',
          metadata: {
            description: 'CAP invite expiry check failed after upload',
            status: 'failed',
            error: expireErr.message,
            upload_id: uploadId,
            trigger: 'cap_data_upload',
          },
        });
      }

      let deactivatedCount = 0;
      try {
        const { data: deactivated, error: deactivateError } = await supabase.rpc('deactivate_cap_dropouts', {
          p_upload_id: uploadId,
        });
        if (deactivateError) throw deactivateError;
        deactivatedCount = deactivated ?? 0;
        await logAdminAction({
          action: 'UPDATE',
          tableName: 'cap_applications',
          metadata: {
            description: `Deactivated ${deactivatedCount} CAP application(s) missing from upload`,
            deactivated_count: deactivatedCount,
            upload_id: uploadId,
            trigger: 'cap_data_upload',
          },
        });
      } catch (deactivateErr: any) {
        await logAdminAction({
          action: 'UPDATE',
          tableName: 'cap_applications',
          metadata: {
            description: 'CAP dropout deactivation failed after upload',
            status: 'failed',
            error: deactivateErr.message,
            upload_id: uploadId,
            trigger: 'cap_data_upload',
          },
        });
      }

      const parts = [`${rowCount.toLocaleString()} rows uploaded`];
      if (matchedCount > 0) parts.push(`${matchedCount} creator(s) activated`);
      if (expiredCount > 0) parts.push(`${expiredCount} invite(s) flagged expired`);
      if (deactivatedCount > 0) parts.push(`${deactivatedCount} creator(s) deactivated (missing from upload)`);
      toast.success(parts.join(' — ') + '.');
    } catch (err: any) {
      toast.error(`Upload failed: ${err.message}`);
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl sm:text-2xl font-bold tracking-tight">CAP Data</h1>
          <p className="text-muted-foreground">Upload Creator Affiliate Program reports for leaderboard data</p>
        </div>
        <Dialog>
          <DialogTrigger asChild>
            <Button variant="outline" size="sm">
              <ImageIcon className="mr-2 h-4 w-4" /> View Expected Format
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-4xl">
            <DialogHeader>
              <DialogTitle>Expected CAP Report Format</DialogTitle>
            </DialogHeader>
            <p className="text-sm text-muted-foreground mb-3">
              Export from TikTok Shop Partner Center → Custom Report → Creator dimension. Required columns: <strong>Date</strong>, <strong>Creator username</strong>, <strong>Affiliate GMV</strong>, <strong>Affiliate orders</strong>, <strong>Affiliate LIVE GMV</strong>.
            </p>
            <img src={capFormatRef} alt="CAP report format reference" className="rounded-lg border w-full" />
          </DialogContent>
        </Dialog>
      </div>

      {/* Upload Section */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <FileSpreadsheet className="h-5 w-5" />
            Upload CAP Report
          </CardTitle>
          <CardDescription>Drag and drop a .xlsx file or click to browse</CardDescription>
        </CardHeader>
        <CardContent>
          {uploadResult && (
            <div className="mb-4 p-4 rounded-lg bg-green-500/10 border border-green-500/20 flex items-start gap-3">
              <CheckCircle2 className="h-5 w-5 text-green-500 mt-0.5 shrink-0" />
              <div>
                <p className="font-medium text-green-600">
                  ✅ {uploadResult.count.toLocaleString()} rows uploaded successfully.
                </p>
                <p className="text-sm text-muted-foreground">
                  {new Date(uploadResult.timestamp).toLocaleString()}
                </p>
              </div>
            </div>
          )}

          {!parsedRows.length && !uploading && (
            parsing ? (
              <div className="border-2 border-dashed rounded-lg p-12 text-center border-primary/40 bg-primary/5">
                <FileSpreadsheet className="mx-auto h-10 w-10 text-primary/60 mb-3 animate-pulse" />
                <p className="text-lg font-medium">Parsing file…</p>
                <p className="text-sm text-muted-foreground mt-1 truncate max-w-xs mx-auto">{selectedFileName}</p>
                <p className="text-xs text-muted-foreground mt-2">Reading rows from your spreadsheet, please wait</p>
              </div>
            ) : (
              <div
                className={`border-2 border-dashed rounded-lg p-12 text-center cursor-pointer transition-colors ${
                  isDragging ? 'border-primary bg-primary/5' : 'border-muted-foreground/25 hover:border-primary/50'
                }`}
                onDragOver={(e) => { e.preventDefault(); setIsDragging(true); }}
                onDragLeave={() => setIsDragging(false)}
                onDrop={handleDrop}
                onClick={() => fileRef.current?.click()}
              >
                <Upload className="mx-auto h-10 w-10 text-muted-foreground/50 mb-3" />
                <p className="text-lg font-medium">Drop your .xlsx file here</p>
                <p className="text-sm text-muted-foreground mt-1">or click to browse</p>
                <input ref={fileRef} type="file" accept=".xlsx" className="hidden" onChange={handleFileChange} />
              </div>
            )
          )}

          {uploading && (
            <div className="space-y-3">
              <p className="text-sm font-medium">Uploading {parsedRows.length.toLocaleString()} rows...</p>
              <Progress value={uploadProgress} className="h-3" />
              <p className="text-sm text-muted-foreground">{uploadProgress}% complete</p>
            </div>
          )}

          {parsedRows.length > 0 && !uploading && (
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <p className="text-sm font-medium">
                  Preview — <span className="text-primary">{parsedRows.length.toLocaleString()}</span> rows to import
                </p>
                <div className="flex gap-2">
                  <Button variant="outline" onClick={() => { setParsedRows([]); if (fileRef.current) fileRef.current.value = ''; }}>
                    <XCircle className="mr-2 h-4 w-4" /> Cancel
                  </Button>
                  <Button onClick={handleUpload}>
                    <Upload className="mr-2 h-4 w-4" /> Upload to Database
                  </Button>
                </div>
              </div>
              <div className="rounded-md border max-h-80 overflow-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Date</TableHead>
                      <TableHead>Creator Username</TableHead>
                      <TableHead className="text-right">Affiliate GMV</TableHead>
                      <TableHead className="text-right">Affiliate Orders</TableHead>
                      <TableHead className="text-right">Affiliate LIVE GMV</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {parsedRows.slice(0, 20).map((row, i) => (
                      <TableRow key={i}>
                        <TableCell>{row.report_date || '—'}</TableCell>
                        <TableCell className="font-medium">{row.creator_username}</TableCell>
                        <TableCell className="text-right">${row.affiliate_gmv.toFixed(2)}</TableCell>
                        <TableCell className="text-right">{row.affiliate_orders.toLocaleString()}</TableCell>
                        <TableCell className="text-right">${row.affiliate_live_gmv.toFixed(2)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
              {parsedRows.length > 20 && (
                <p className="text-sm text-muted-foreground">Showing 20 of {parsedRows.length.toLocaleString()} rows</p>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Upload History */}
      <Card>
        <CardHeader className="flex flex-row items-start justify-between">
          <div>
            <CardTitle className='mb-2'>Upload History</CardTitle>
            <CardDescription>Previous CAP data uploads</CardDescription>
          </div>
          <Button size="sm" variant="outline" onClick={handleExport} disabled={exporting} className="gap-2 shrink-0">
            <Download className="h-4 w-4" />
            {exporting ? 'Exporting…' : 'Export'}
          </Button>
        </CardHeader>
        <CardContent className="space-y-4">
          {exporting && (
            <div className="space-y-1">
              <p className="text-xs text-muted-foreground">
                {exportProgress < 90 ? `Fetching records… ${exportProgress}%` : 'Building file…'}
              </p>
              <Progress value={exportProgress} className="h-1.5" />
            </div>
          )}
          <div className="flex flex-wrap gap-3 items-end">
            <div className="flex flex-col gap-1">
              <label className="text-xs text-muted-foreground">From</label>
              <Input
                type="date"
                value={historyDateFrom}
                onChange={e => setHistoryDateFrom(e.target.value)}
                className="h-8 w-40"
              />
            </div>
            <div className="flex flex-col gap-1">
              <label className="text-xs text-muted-foreground">To</label>
              <Input
                type="date"
                value={historyDateTo}
                onChange={e => setHistoryDateTo(e.target.value)}
                className="h-8 w-40"
              />
            </div>
            <Button
              size="sm"
              onClick={() => {
                setHistoryPage(1);
                loadUploadHistory(1, historyDateFrom, historyDateTo, historyPageSize);
              }}
              disabled={!historyDateFrom && !historyDateTo}
            >
              Filter
            </Button>
            {(historyDateFrom || historyDateTo) && (
              <Button
                size="sm"
                variant="outline"
                onClick={() => {
                  setHistoryDateFrom('');
                  setHistoryDateTo('');
                  setHistoryPage(1);
                  loadUploadHistory(1, '', '', historyPageSize);
                }}
              >
                Clear
              </Button>
            )}
          </div>

          {uploadHistory?.length === 0 ? (
            <p className="text-sm text-muted-foreground">No uploads found</p>
          ) : (
            <>
              <div className="rounded-md border">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Upload Date</TableHead>
                      <TableHead className="text-right">Row Count</TableHead>
                      <TableHead>Status</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {uploadHistory?.map((h, i) => (
                      <TableRow key={h.uploaded_at}>
                        <TableCell className="whitespace-nowrap">{new Date(h.uploaded_at).toLocaleString()}</TableCell>
                        <TableCell className="text-right">{h.row_count.toLocaleString()}</TableCell>
                        <TableCell>
                          {historyPage === 1 && i === 0 ? (
                            <Badge variant="default">Active</Badge>
                          ) : (
                            <Badge variant="secondary">Previous</Badge>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
              <DataPagination
                currentPage={historyPage}
                totalPages={Math.ceil(historyTotal / historyPageSize)}
                pageSize={historyPageSize}
                totalItems={historyTotal}
                onPageChange={(p) => {
                  setHistoryPage(p);
                  loadUploadHistory(p, historyDateFrom, historyDateTo, historyPageSize);
                }}
                onPageSizeChange={(size) => {
                  setHistoryPageSize(size);
                  setHistoryPage(1);
                  loadUploadHistory(1, historyDateFrom, historyDateTo, size);
                }}
              />
            </>
          )}
        </CardContent>
      </Card>

      {/* Leaderboard Manager */}
      <LeaderboardManager queryClient={queryClient} editingRow={editingRow} setEditingRow={setEditingRow} />
    </div>
  );
}

function LeaderboardManager({ queryClient, editingRow, setEditingRow }: { queryClient: any; editingRow: any; setEditingRow: (r: any) => void }) {
  const { data: rows, isLoading, refetch } = useQuery({
    queryKey: ['leaderboard'],
    queryFn: async () => {
      const { data } = await supabase.from('leaderboard').select('*').order('rank');
      return data ?? [];
    },
  });

  const headers = rows?.[0] ? Object.keys(rows[0]) : [];

  const updateMutation = useMutation({
    mutationFn: async (row: any) => {
      const { id, ...rest } = row;
      
      // Fetch old data for audit log
      const { data: oldData } = await supabase.from('leaderboard').select('*').eq('id', id).single();
      
      const { error } = await supabase.from('leaderboard').update(rest).eq('id', id);
      if (error) throw error;

      // Audit log
      if (oldData) {
        await logUpdate('leaderboard', id, oldData, { ...rest, id }, {
          description: `Updated leaderboard row for "${rest.creator_username || oldData.creator_username}"`,
          metadata: {
            creator_username: rest.creator_username || oldData.creator_username
          }
        });
      }
    },
    onSuccess: () => {
      toast.success('Row updated');
      setEditingRow(null);
      queryClient.invalidateQueries({ queryKey: ['leaderboard'] });
    },
    onError: (err: any) => toast.error(err.message),
  });

  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      // Fetch old data for audit log
      const { data: oldData } = await supabase.from('leaderboard').select('*').eq('id', id).single();
      
      const { error } = await supabase.from('leaderboard').delete().eq('id', id);
      if (error) throw error;

      // Audit log
      if (oldData) {
        await logDelete('leaderboard', id, oldData, {
          description: `Deleted leaderboard row for "${oldData.creator_username}"`,
          metadata: {
            creator_username: oldData.creator_username
          }
        });
      }
    },
    onSuccess: () => {
      toast.success('Row deleted');
      queryClient.invalidateQueries({ queryKey: ['leaderboard'] });
    },
    onError: (err: any) => toast.error(err.message),
  });

  return (
    <>
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Trophy className="h-5 w-5" />
            Upload Leaderboard CSV
          </CardTitle>
          <CardDescription>Upload CSV data directly into the leaderboard table (append or replace)</CardDescription>
        </CardHeader>
        <CardContent>
          <CSVUploader tableName="leaderboard" onComplete={() => refetch()} />
        </CardContent>
      </Card>
      <Card>
        <CardHeader>
          <CardTitle className="text-xl sm:text-2xl">Current Leaderboard ({rows?.length ?? 0} entries)</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="max-h-96 overflow-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  {headers.map(h => <TableHead key={h}>{h}</TableHead>)}
                  <TableHead className="w-24">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {isLoading ? (
                  <TableRow>
                    <TableCell colSpan={headers.length + 1} className="h-24 text-center text-muted-foreground">
                      Loading...
                    </TableCell>
                  </TableRow>
                ) : !rows || rows.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={headers.length + 1} className="h-24 text-center text-muted-foreground">
                      No leaderboard data
                    </TableCell>
                  </TableRow>
                ) : (
                  rows?.map((row: any) => (
                  <TableRow key={row.id}>
                    {headers.map(h => (
                      <TableCell key={h}>
                        {editingRow?.id === row.id ? (
                          <Input
                            value={editingRow[h] ?? ''}
                            onChange={e => setEditingRow({ ...editingRow, [h]: e.target.value })}
                            className="h-8"
                            disabled={h === 'id'}
                          />
                        ) : (
                          <span className="max-w-[150px] truncate block">{String(row[h] ?? '')}</span>
                        )}
                      </TableCell>
                    ))}
                    <TableCell>
                      {editingRow?.id === row.id ? (
                        <div className="flex gap-1">
                          <Button variant="ghost" size="icon" onClick={() => updateMutation.mutate(editingRow)}><Check className="h-4 w-4" /></Button>
                          <Button variant="ghost" size="icon" onClick={() => setEditingRow(null)}><X className="h-4 w-4" /></Button>
                        </div>
                      ) : (
                        <div className="flex gap-1">
                          <Button variant="ghost" size="icon" onClick={() => setEditingRow({ ...row })}><Pencil className="h-4 w-4" /></Button>
                          <Button variant="ghost" size="icon" onClick={() => deleteMutation.mutate(row.id)}><Trash2 className="h-4 w-4 text-destructive" /></Button>
                        </div>
                      )}
                    </TableCell>
                  </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>
    </>
  );
}
